import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import QrScanner from 'qr-scanner';
import { attendeesAPI } from '../services/api';

const AccessControl = () => {
  const { projectId } = useParams();
  const [mode, setMode] = useState(null); // 'qr' or 'facial'
  const [attendee, setAttendee] = useState(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [stream, setStream] = useState(null);
  const videoRef = useRef(null);
  const qrVideoRef = useRef(null);
  const scannerRef = useRef(null);
  const processingRef = useRef(false);
  const timeoutIdsRef = useRef([]);
  const isMountedRef = useRef(true);

   // Limpiar cuando se desmonte el componente
   useEffect(() => {
     return () => {
       isMountedRef.current = false;
       timeoutIdsRef.current.forEach(id => {
         clearTimeout(id);
       });
       timeoutIdsRef.current = [];
       if (scannerRef.current) {
         scannerRef.current.destroy();
         scannerRef.current = null;
       }
     };
   }, []);

   // Limpiar stream cuando se desmonte
   useEffect(() => {
     return () => {
       if (stream) {
         stream.getTracks().forEach(track => {
           track.stop();
         });
       }
     };
   }, [stream]);

  const addTimeout = (callback, delay) => {
    const id = setTimeout(() => {
      timeoutIdsRef.current = timeoutIdsRef.current.filter(t => t !== id);
      if (isMountedRef.current) callback();
    }, delay);
    timeoutIdsRef.current.push(id);
    return id;
  };

  const onScanSuccess = useCallback(async (result) => {
    // Evitar procesar múltiples escaneos simultáneos
    if (processingRef.current) return;
    processingRef.current = true;

    const decodedText = result.data;
    console.log('🔍 [ACCESS] QR detectado:', decodedText);
    setLoading(true);

    try {
      const response = await attendeesAPI.checkInByQR(decodedText);
      console.log('✅ [ACCESS] Check-in exitoso:', response.data);

      setAttendee(response.data.attendee);
      setMessage(`¡Bienvenido ${response.data.attendee.full_name || response.data.attendee.fullName}!`);

      // Detener el escáner
      if (scannerRef.current) {
        scannerRef.current.destroy();
        scannerRef.current = null;
      }

      // Reproducir audio de bienvenida con ElevenLabs (igual que reconocimiento facial)
      if (response.data.welcomeAudio && response.data.welcomeAudio.url) {
        console.log('🔊 [ACCESS-QR] Reproduciendo mensaje de bienvenida:', response.data.welcomeAudio.message);
        const audio = new Audio(response.data.welcomeAudio.url);
        audio.play()
          .then(() => console.log('✅ [ACCESS-QR] Audio reproducido exitosamente'))
          .catch((err) => console.warn('⚠️  [ACCESS-QR] No se pudo reproducir audio:', err));
      } else {
        console.log('ℹ️  [ACCESS-QR] No hay audio de bienvenida configurado para este proyecto');
      }

      // Resetear después de 5 segundos y volver a pantalla inicial
      addTimeout(() => {
        setAttendee(null);
        setMessage('');
        setMode(null);
        processingRef.current = false;
      }, 5000);
    } catch (error) {
      console.error('❌ [ACCESS] Error en check-in:', error);

      // Detener el escáner si está activo
      if (scannerRef.current) {
        scannerRef.current.stop();
        scannerRef.current.destroy();
        scannerRef.current = null;
      }

      // Limpiar modo inmediatamente
      setMode(null);

      const errorMsg = error.response?.data?.error || 'Error en el check-in. Por favor intenta nuevamente.';

      // Mensaje personalizado para check-in duplicado
      if (errorMsg.includes('ya') && (errorMsg.includes('check-in') || errorMsg.includes('registrado'))) {
        setMessage('✅ ' + errorMsg);
        if (error.response?.data?.attendee) {
          setAttendee(error.response.data.attendee);
        }
      } else {
        setMessage(errorMsg);
      }

      // Resetear mensaje de error y modo después de 3 segundos
      addTimeout(() => {
        setMessage('');
        setMode(null);
        setAttendee(null);
        processingRef.current = false;
      }, 3000);
    } finally {
      setLoading(false);
    }
  }, []);

  const startQRScanner = () => {
    console.log('📱 [ACCESS] Iniciando escáner QR');
    setMode('qr');
    setMessage('');
    setAttendee(null);
    processingRef.current = false;

    // Esperar a que el video element se renderice
    addTimeout(() => {
      if (!qrVideoRef.current) {
        console.error('❌ [ACCESS] Video element no encontrado');
        return;
      }

      const scanner = new QrScanner(
        qrVideoRef.current,
        onScanSuccess,
        {
          returnDetailedScanResult: true,
          highlightScanRegion: true,
          highlightCodeOutline: true,
          preferredCamera: 'environment',
          maxScansPerSecond: 5,
        }
      );

      scannerRef.current = scanner;

      scanner.start()
        .then(() => {
          console.log('✅ [ACCESS] Escáner QR iniciado');
        })
        .catch((err) => {
          console.error('❌ [ACCESS] Error iniciando escáner:', err);
          setMessage('No se pudo acceder a la cámara. Verifica los permisos.');
        });
    }, 100);
  };

  const startFacialRecognition = async () => {
    console.log('😊 [ACCESS] Iniciando reconocimiento facial');
    setMode('facial');
    setMessage('');
    setAttendee(null);
    setLoading(true);

    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
      console.log('✅ [ACCESS] Cámara iniciada');
    } catch (error) {
      console.error('❌ [ACCESS] Error accediendo a la cámara:', error);
      alert('No se pudo acceder a la cámara. Por favor verifica los permisos.');
      setMode(null);
    } finally {
      setLoading(false);
    }
  };

  const captureFacial = async () => {
    const startTime = Date.now();
    console.log('📸 [ACCESS] Capturando rostro para verificación');
    setLoading(true);

    const canvas = document.createElement('canvas');

    // Reducir resolución para procesamiento ultrarrápido (máximo 320x240)
    const maxWidth = 320;
    const maxHeight = 240;
    const videoWidth = videoRef.current.videoWidth;
    const videoHeight = videoRef.current.videoHeight;

    let width = videoWidth;
    let height = videoHeight;

    // Mantener aspect ratio
    const ratio = Math.min(maxWidth / width, maxHeight / height);
    width = Math.floor(width * ratio);
    height = Math.floor(height * ratio);

    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    ctx.drawImage(videoRef.current, 0, 0, width, height);

    const captureTime = Date.now() - startTime;
    console.log(`⏱️ [ACCESS] Captura completada en ${captureTime}ms`);

    canvas.toBlob(async (blob) => {
      try {
        const file = new File([blob], `checkin-${Date.now()}.jpg`, { type: 'image/jpeg' });
        console.log('🚀 [ACCESS] Enviando foto al servidor (tamaño:', Math.round(blob.size / 1024), 'KB, resolución:', width, 'x', height, ')');

        const sendStartTime = Date.now();
        const response = await attendeesAPI.checkInByFacial(projectId, file);
        const totalTime = Date.now() - sendStartTime;
        console.log(`✅ [ACCESS] Reconocimiento exitoso en ${totalTime}ms`);

        setAttendee(response.data.attendee);
        setMessage(`¡Bienvenido ${response.data.attendee.full_name}!`);

        // Reproducir audio de bienvenida con ElevenLabs
        if (response.data.welcomeAudio && response.data.welcomeAudio.url) {
          console.log('🔊 [ACCESS] Reproduciendo mensaje de bienvenida:', response.data.welcomeAudio.message);
          const audio = new Audio(response.data.welcomeAudio.url);
          audio.play()
            .then(() => console.log('✅ [ACCESS] Audio reproducido exitosamente'))
            .catch((err) => console.warn('⚠️  [ACCESS] No se pudo reproducir audio:', err));
        } else {
          console.log('ℹ️  [ACCESS] No hay audio de bienvenida configurado para este proyecto');
        }

        // Detener cámara
        if (stream) {
          stream.getTracks().forEach(track => track.stop());
          setStream(null);
        }

        // Resetear después de 5 segundos
        addTimeout(() => {
          setAttendee(null);
          setMessage('');
          setMode(null);
        }, 5000);
      } catch (error) {
        const totalTime = Date.now() - startTime;
        console.error(`❌ [ACCESS] Error después de ${totalTime}ms:`, error);
        const errorMsg = error.response?.data?.error || 'No se reconoció el rostro. Por favor intenta nuevamente.';

        // Mensaje personalizado para check-in duplicado
        if (errorMsg.includes('ya') && errorMsg.includes('registrado')) {
          setMessage('✅ Ya tienes check-in registrado. ¡Disfruta el evento!');
        } else {
          setMessage(errorMsg);
        }

        // Resetear mensaje después de 4 segundos
        addTimeout(() => {
          setMessage('');
          setMode(null);
          if (stream) {
            stream.getTracks().forEach(track => track.stop());
            setStream(null);
          }
        }, 4000);
      } finally {
        setLoading(false);
      }
    }, 'image/jpeg', 0.70);
  };

  const resetMode = () => {
    console.log('🔄 [ACCESS] Reseteando modo');
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    if (scannerRef.current) {
      scannerRef.current.destroy();
      scannerRef.current = null;
    }
    setMode(null);
    setMessage('');
    setAttendee(null);
    processingRef.current = false;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-500 via-blue-500 to-purple-600">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-600 to-blue-600 text-white p-8 text-center">
            <h1 className="text-4xl font-bold mb-2">Control de Acceso</h1>
            <p className="text-lg opacity-90">Selecciona un método de verificación</p>
          </div>

          <div className="p-8">
            {/* Selector de modo */}
            {!mode && !attendee && !message && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <button
                  onClick={startQRScanner}
                  className="group relative bg-gradient-to-br from-blue-500 to-blue-600 text-white p-12 rounded-2xl hover:from-blue-600 hover:to-blue-700 transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-2xl"
                >
                  <div className="text-7xl mb-4 transform group-hover:scale-110 transition-transform">📱</div>
                  <div className="text-2xl font-bold mb-2">Escanear QR</div>
                  <div className="text-sm opacity-90">Escanea el código del boarding pass</div>
                </button>

                <button
                  onClick={startFacialRecognition}
                  className="group relative bg-gradient-to-br from-green-500 to-green-600 text-white p-12 rounded-2xl hover:from-green-600 hover:to-green-700 transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-2xl"
                >
                  <div className="text-7xl mb-4 transform group-hover:scale-110 transition-transform">😊</div>
                  <div className="text-2xl font-bold mb-2">Reconocimiento Facial</div>
                  <div className="text-sm opacity-90">Verifica tu identidad con tu rostro</div>
                </button>
              </div>
            )}

            {/* Escáner QR */}
            {mode === 'qr' && !attendee && !message && (
              <div>
                <div className="mb-6 text-center">
                  <h2 className="text-2xl font-bold text-gray-800 mb-2">Escaneando código QR</h2>
                  <p className="text-gray-600">Coloca el código QR frente a la cámara</p>
                </div>
                <div className="qr-scanner-container mb-6 rounded-xl overflow-hidden bg-black w-full">
                  <video ref={qrVideoRef} className="w-full rounded-xl" style={{ minHeight: '400px', objectFit: 'cover' }} />
                </div>
                {loading && (
                  <div className="text-center mb-4">
                    <div className="inline-block w-8 h-8 border-4 border-t-transparent border-blue-500 rounded-full animate-spin"></div>
                    <p className="mt-2 text-gray-600">Verificando...</p>
                  </div>
                )}
                <button
                  onClick={resetMode}
                  className="w-full py-3 px-6 bg-gray-500 text-white rounded-xl hover:bg-gray-600 transition-colors font-semibold"
                >
                  ← Volver
                </button>
              </div>
            )}

            {/* Reconocimiento Facial */}
            {mode === 'facial' && !attendee && !message && (
              <div>
                <div className="mb-6 text-center">
                  <h2 className="text-2xl font-bold text-gray-800 mb-2">Reconocimiento Facial</h2>
                  <p className="text-gray-600">Centra tu rostro en la cámara</p>
                </div>

                <div className="relative mb-6 mx-auto">
                  <div className="rounded-2xl overflow-hidden shadow-2xl bg-black">
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      className="w-full"
                      style={{ transform: 'scaleX(-1)' }}
                    />

                    {loading && (
                      <div className="absolute inset-0 bg-black bg-opacity-70 flex items-center justify-center">
                        <div className="text-center text-white">
                          <div className="inline-block w-16 h-16 border-4 border-t-transparent border-white rounded-full animate-spin mb-4"></div>
                          <p className="text-lg font-semibold">Verificando identidad...</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-3">
                  <button
                    onClick={captureFacial}
                    disabled={loading}
                    className="w-full py-4 px-8 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl hover:from-green-600 hover:to-green-700 transition-all font-bold text-lg shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Verificando...' : 'Verificar Identidad'}
                  </button>
                  <button
                    onClick={resetMode}
                    disabled={loading}
                    className="w-full py-3 px-6 bg-gray-500 text-white rounded-xl hover:bg-gray-600 transition-colors font-semibold disabled:opacity-50"
                  >
                    ← Volver
                  </button>
                </div>
              </div>
            )}

            {/* Mensaje de resultado */}
            {message && (
              <div className={`mt-6 p-6 rounded-2xl text-center transform transition-all duration-500 ${
                attendee
                  ? 'bg-gradient-to-r from-green-100 to-green-200 border-2 border-green-500 scale-105'
                  : 'bg-gradient-to-r from-red-100 to-red-200 border-2 border-red-500'
              }`}>
                {attendee ? (
                  <>
                    <div className="text-6xl mb-4 animate-bounce">✅</div>
                    <p className="text-3xl font-bold text-green-800 mb-4">{message}</p>
                    <div className="bg-white bg-opacity-80 rounded-xl p-6 mt-4">
                      <p className="text-2xl font-bold text-gray-800 mb-2">{attendee.full_name || attendee.fullName}</p>
                      {attendee.company && (
                        <p className="text-lg text-gray-600 mb-2">{attendee.company}</p>
                      )}
                      {attendee.email && (
                        <p className="text-sm text-gray-500">{attendee.email}</p>
                      )}
                      <div className="mt-4 pt-4 border-t border-gray-300">
                        <p className="text-sm text-gray-600">Check-in exitoso</p>
                        <p className="text-xs text-gray-500 mt-1">{new Date().toLocaleString('es-ES')}</p>
                      </div>
                    </div>
                    <button
                      onClick={resetMode}
                      className="w-full py-3 px-6 bg-gray-500 text-white rounded-xl hover:bg-gray-600 transition-colors font-semibold mt-6"
                    >
                      ← Volver al inicio
                    </button>
                  </>
                ) : (
                  <>
                    <div className="text-6xl mb-4">❌</div>
                    <p className="text-2xl font-bold text-red-800">{message}</p>
                    <button
                      onClick={resetMode}
                      className="w-full py-3 px-6 bg-gray-500 text-white rounded-xl hover:bg-gray-600 transition-colors font-semibold mt-6"
                    >
                      ← Volver al inicio
                    </button>
                  </>
                )}
              </div>
            )}

            {/* Estado de carga global */}
            {loading && !mode && (
              <div className="text-center py-12">
                <div className="inline-block w-16 h-16 border-4 border-t-transparent border-blue-500 rounded-full animate-spin mb-4"></div>
                <p className="text-lg text-gray-600">Cargando...</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccessControl;
